/**
 * client/hud/gfx.ts — 2D drawing for the HUD and menus: BO2 art loader, blend modes, tinting,
 * bitmap-font text. Canvas 2D, coordinates in LUI virtual units (the caller sets the transform).
 *
 * OWNER: HUD-UI-AUDIO agent.
 *
 * - Art comes from game/public/assets/ui (served at /assets/ui/). Images whose material blend is
 *   "add" in ui-manifest.json have black backgrounds by design and must be drawn additively
 *   (globalCompositeOperation 'lighter') [hud.md "Asset index"]. The few additive images the HUD
 *   uses are listed in ADDITIVE below (copied from the manifest; checked by gfx.test.ts).
 * - LUI setRGB tints multiply the texture colour. Canvas has no per-draw tint, so tinted copies
 *   are cached per (image, quantised colour).
 * - Bitmap fonts: one alpha atlas, tinted per colour (cached); distFont is a signed-distance
 *   atlas that we threshold once into a plain alpha atlas (edge at alpha ~118/255, picked by eye
 *   against the atlas; ESTIMATE of the engine's SDF shader threshold).
 */
import { type FontData, type LuiFont, LUI_FONTS, glyphFor, measureText, parseFont } from './bmfont';

export const UI_BASE = '/assets/ui/';

/** Materials with blend "add" in ui-manifest.json that the HUD draws. */
export const ADDITIVE = new Set<string>([
  'hud/hud_mp_vis_left_lower.png',
  'hud/hud_mp_vis_right_lower.png',
  'hud/hud_mp_vis_gun_title_left.png',
  'hud/hud_mp_vis_gun_title_mid.png',
  'hud/hud_mp_vis_gun_title_right.png',
  'hud/hud_mp_vis_score_highlight.png',
  'medal/hud_lui_scorefeed_glow.png',
  'medal/hud_lui_medal_glow.png',
  'damage/hit_direction.png',
  'weapon/hud_mp_firerate_bolt.png',
  'weapon/hud_mp_firerate_free.png',
]);

export type RGB = readonly [number, number, number];
export const WHITE: RGB = [1, 1, 1];
export const BLACK: RGB = [0, 0, 0];

// Colours [hud.md "Colours", codbase.lua:222-328, hud-layout.json colors]
export const COLORS = {
  offWhite: [1, 1, 1] as RGB,
  BOIIOrange: [1, 0.4, 0] as RGB,
  playerYellow: [0.92, 0.8, 0.31] as RGB,
  red: [0.73, 0.25, 0.25] as RGB,
  brightRed: [1, 0.19, 0.19] as RGB,
  green: [0.42, 0.68, 0.46] as RGB,
  yellowGlow: [0.9, 0.69, 0.2] as RGB,
  blue: [0.35, 0.63, 0.75] as RGB,
  gray: [0.39, 0.38, 0.36] as RGB,
  // CoD.ScoreFeed.Color1 = blue * 0.25, Color2, Color3 (scorefeed.lua:31-45)
  scoreFeed1: [0.35 * 0.25, 0.63 * 0.25, 0.75 * 0.25] as RGB,
  scoreFeed2: [0.07, 0.04, 0.05] as RGB,
  scoreFeed3: [0.06, 0, 0.12] as RGB,
  wagerInTheMoney: [0.42, 0.68, 0.46] as RGB,
  wagerOutOfTheMoney: [0.73, 0.29, 0.19] as RGB,
};

function colorKey(c: RGB): string {
  // quantise to 1/64 so animated tints reuse cache entries
  return `${Math.round(c[0] * 64)},${Math.round(c[1] * 64)},${Math.round(c[2] * 64)}`;
}
function cssColor(c: RGB): string {
  return `rgb(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)})`;
}

type Drawable = HTMLImageElement | HTMLCanvasElement;

/** Shared image/font cache (one per page). */
export class UiAssets {
  private readonly images = new Map<string, HTMLImageElement>();
  private readonly failed = new Set<string>();
  private readonly tinted = new Map<string, HTMLCanvasElement>();
  private readonly fonts = new Map<string, FontData>();
  private readonly fontLoads = new Map<string, Promise<FontData | null>>();
  private readonly atlases = new Map<string, Drawable>();

  /** Start loading (idempotent). Returns the element once decoded, else null. */
  img(path: string): HTMLImageElement | null {
    let im = this.images.get(path);
    if (!im) {
      if (this.failed.has(path)) return null;
      im = new Image();
      im.decoding = 'async';
      im.onerror = () => { this.failed.add(path); this.images.delete(path); };
      im.src = UI_BASE + path;
      this.images.set(path, im);
    }
    return im.complete && im.naturalWidth > 0 ? im : null;
  }

  preload(paths: readonly string[]): Promise<void> {
    return Promise.all(paths.map((p) => new Promise<void>((res) => {
      const im = this.images.get(p);
      if (im?.complete) { res(); return; }
      this.img(p);
      const el = this.images.get(p);
      if (!el) { res(); return; }
      el.addEventListener('load', () => res(), { once: true });
      el.addEventListener('error', () => res(), { once: true });
    }))).then(() => undefined);
  }

  /** Image multiplied by an RGB tint (LUI setRGB). White returns the image itself. */
  tintedImg(path: string, c: RGB): Drawable | null {
    const im = this.img(path);
    if (!im) return null;
    if (c[0] >= 0.999 && c[1] >= 0.999 && c[2] >= 0.999) return im;
    const key = `${path}|${colorKey(c)}`;
    let cv = this.tinted.get(key);
    if (!cv) {
      if (this.tinted.size > 400) this.tinted.clear();
      cv = document.createElement('canvas');
      cv.width = im.naturalWidth; cv.height = im.naturalHeight;
      const g = cv.getContext('2d')!;
      g.drawImage(im, 0, 0);
      g.globalCompositeOperation = 'multiply';
      g.fillStyle = cssColor(c);
      g.fillRect(0, 0, cv.width, cv.height);
      g.globalCompositeOperation = 'destination-in';
      g.drawImage(im, 0, 0);
      this.tinted.set(key, cv);
    }
    return cv;
  }

  // ---- fonts ------------------------------------------------------------------------------
  loadFont(file: string): Promise<FontData | null> {
    let p = this.fontLoads.get(file);
    if (!p) {
      p = fetch(`${UI_BASE}fonts/${file}.json`)
        .then((r) => (r.ok ? r.json() : null))
        .then((raw) => {
          if (!raw) return null;
          const f = parseFont(raw);
          this.fonts.set(file, f);
          this.img(`fonts/${f.atlas}`);
          return f;
        })
        .catch(() => null);
      this.fontLoads.set(file, p);
    }
    return p;
  }

  loadAllFonts(): Promise<void> {
    const files = new Set(Object.values(LUI_FONTS).map((f) => f.file));
    return Promise.all([...files].map((f) => this.loadFont(f))).then(async (fs) => {
      await this.preload(fs.filter((f): f is FontData => !!f).map((f) => `fonts/${f.atlas}`));
    });
  }

  font(lui: LuiFont): FontData | null {
    const f = this.fonts.get(LUI_FONTS[lui].file);
    if (!f) void this.loadFont(LUI_FONTS[lui].file);
    return f ?? null;
  }

  /** Atlas tinted to a colour (alpha atlas -> coloured glyphs). */
  private atlas(font: FontData, c: RGB): Drawable | null {
    const key = `${font.atlas}|${colorKey(c)}`;
    let a = this.atlases.get(key);
    if (a) return a;
    const im = this.img(`fonts/${font.atlas}`);
    if (!im) return null;
    const cv = document.createElement('canvas');
    cv.width = im.naturalWidth; cv.height = im.naturalHeight;
    const g = cv.getContext('2d', { willReadFrequently: font.sdf })!;
    g.drawImage(im, 0, 0);
    if (font.sdf) {
      // signed-distance atlas -> crisp alpha (ESTIMATE threshold, see header)
      const d = g.getImageData(0, 0, cv.width, cv.height);
      const px = d.data;
      for (let i = 0; i < px.length; i += 4) {
        const t = (px[i + 3]! - 106) / 24;
        const s = t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t);
        px[i] = 255; px[i + 1] = 255; px[i + 2] = 255; px[i + 3] = Math.round(s * 255);
      }
      g.putImageData(d, 0, 0);
    }
    g.globalCompositeOperation = 'source-in';
    g.fillStyle = cssColor(c);
    g.fillRect(0, 0, cv.width, cv.height);
    if (this.atlases.size > 48) this.atlases.clear();
    this.atlases.set(key, cv);
    a = cv;
    return a;
  }

  measure(lui: LuiFont, text: string, size = LUI_FONTS[lui].size): number {
    const f = this.font(lui);
    return f ? measureText(f, text, size) : text.length * size * 0.4;
  }

  /**
   * Draw text whose box top is at y (baseline at y + size). align: x is the left edge, centre
   * or right edge. Returns the drawn width.
   */
  text(ctx: CanvasRenderingContext2D, lui: LuiFont, text: string, x: number, y: number, opts: TextOpts = {}): number {
    const f = this.font(lui);
    if (!f) return 0;
    const size = opts.size ?? LUI_FONTS[lui].size;
    const s = size / f.pixelHeight;
    const w = measureText(f, text, size);
    let px = x;
    if (opts.align === 'center') px -= w / 2;
    else if (opts.align === 'right') px -= w;
    const atlas = this.atlas(f, opts.color ?? WHITE);
    if (!atlas) return w;
    const base = y + size;
    const prevA = ctx.globalAlpha;
    ctx.globalAlpha = prevA * (opts.alpha ?? 1);
    if (opts.shadow) {
      // LUI drop shadow: same text in black at alpha 0.5, offset +2,+2 (notificationpopups.lua CreateText)
      const sh = this.atlas(f, BLACK);
      if (sh) {
        ctx.globalAlpha = prevA * (opts.alpha ?? 1) * opts.shadow;
        this.glyphRun(ctx, f, sh, text, px + 2, base + 2, s);
        ctx.globalAlpha = prevA * (opts.alpha ?? 1);
      }
    }
    this.glyphRun(ctx, f, atlas, text, px, base, s);
    ctx.globalAlpha = prevA;
    return w;
  }

  private glyphRun(ctx: CanvasRenderingContext2D, f: FontData, atlas: Drawable, text: string, x: number, base: number, s: number): void {
    for (const ch of text) {
      const g = glyphFor(f, ch);
      if (!g) continue;
      if (g.w > 0 && g.h > 0) ctx.drawImage(atlas, g.x, g.y, g.w, g.h, x + g.xoffset * s, base + g.yoffset * s, g.w * s, g.h * s);
      x += g.xadvance * s;
    }
  }
}

export interface TextOpts {
  size?: number;
  color?: RGB;
  alpha?: number;
  align?: 'left' | 'center' | 'right';
  /** Drop shadow alpha (black, +2,+2). */
  shadow?: number;
}

export interface ImageOpts {
  alpha?: number;
  tint?: RGB;
  /** Force additive (otherwise taken from ADDITIVE). */
  additive?: boolean;
  /** Source rect in image pixels. */
  src?: [number, number, number, number];
  flipX?: boolean;
  /** Clip rect (virtual units, current transform) — needed because additive draws go to another layer. */
  clip?: [number, number, number, number];
}

/**
 * Additive layer. The HUD canvas sits over the WebGL canvas, so 'lighter' on it would add onto
 * transparent pixels (= opaque black). Additive art therefore goes to a second canvas composited
 * with CSS mix-blend-mode: plus-lighter, i.e. really added to the game image like the engine does.
 */
const additiveTargets = new WeakMap<CanvasRenderingContext2D, CanvasRenderingContext2D>();
export function setAdditiveTarget(ctx: CanvasRenderingContext2D, add: CanvasRenderingContext2D | null): void {
  if (add) additiveTargets.set(ctx, add); else additiveTargets.delete(ctx);
}

/** Draw an image into the rect (virtual units). */
export function drawImage(ctx: CanvasRenderingContext2D, assets: UiAssets, path: string, x: number, y: number, w: number, h: number, o: ImageOpts = {}): void {
  if (w === 0 || h === 0) return;
  const alpha = o.alpha ?? 1;
  if (alpha <= 0.001) return;
  const im = o.tint ? assets.tintedImg(path, o.tint) : assets.img(path);
  if (!im) return;
  const add = o.additive ?? ADDITIVE.has(path);
  const target = add ? additiveTargets.get(ctx) : undefined;
  if (target) {
    target.save();
    target.setTransform(ctx.getTransform());
    target.globalAlpha = ctx.globalAlpha;
    if (o.clip) { target.beginPath(); target.rect(o.clip[0], o.clip[1], o.clip[2], o.clip[3]); target.clip(); }
    drawImage(target, assets, path, x, y, w, h, { ...o, additive: true, clip: undefined });
    target.restore();
    return;
  }
  const prevA = ctx.globalAlpha, prevOp = ctx.globalCompositeOperation;
  ctx.globalAlpha = prevA * alpha;
  if (add) ctx.globalCompositeOperation = 'lighter';
  if (o.flipX) {
    ctx.save();
    ctx.translate(x + w, y);
    ctx.scale(-1, 1);
    x = 0; y = 0;
  }
  if (o.src) ctx.drawImage(im, o.src[0], o.src[1], o.src[2], o.src[3], x, y, w, h);
  else ctx.drawImage(im, x, y, w, h);
  if (o.flipX) ctx.restore();
  ctx.globalAlpha = prevA;
  ctx.globalCompositeOperation = prevOp;
}

let sharedAssets: UiAssets | null = null;
/** One asset cache for HUD + menus. */
export function uiAssets(): UiAssets {
  return (sharedAssets ??= new UiAssets());
}
